import * as React from "react";
import { Pencil } from "lucide-react";
import { Input } from "@/components/ui";
import { cn } from "@/lib/utils";

interface EditableNameProps {
  value: string;
  onSave: (name: string) => Promise<void>;
  disabled?: boolean;
  autoFocusSelect?: boolean;
}

const MAX_LENGTH = 120;
const COPY_SUFFIX = "(Copy)";

/** Inline-editable design name: Enter or blur saves, Esc cancels. */
export const EditableName: React.FC<EditableNameProps> = ({ value, onSave, disabled, autoFocusSelect }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selectCopySuffix = React.useRef(false);
  // Enter saves and then blurs; this keeps the blur from saving a second time.
  const settled = React.useRef(false);

  const startEditing = React.useCallback(
    (selectSuffix: boolean) => {
      setDraft(value);
      setError(null);
      settled.current = false;
      selectCopySuffix.current = selectSuffix;
      setEditing(true);
    },
    [value]
  );

  // Only when the flag turns on (entering right after a duplicate), not on every name change.
  const startEditingRef = React.useRef(startEditing);
  startEditingRef.current = startEditing;
  React.useEffect(() => {
    if (autoFocusSelect && !disabled) startEditingRef.current(true);
  }, [autoFocusSelect, disabled]);

  React.useLayoutEffect(() => {
    const input = inputRef.current;
    if (!editing || !input) return;
    input.focus();
    const start = selectCopySuffix.current ? input.value.lastIndexOf(COPY_SUFFIX) : -1;
    if (start >= 0) input.setSelectionRange(start, start + COPY_SUFFIX.length);
    else input.select();
  }, [editing]);

  const save = async () => {
    if (settled.current) return;
    const name = draft.trim();
    if (name.length < 1 || name.length > MAX_LENGTH) {
      setError("Names must be 1 to 120 characters.");
      return;
    }
    settled.current = true;
    setEditing(false);
    setError(null);
    if (name !== value) await onSave(name).catch(() => undefined);
  };

  const cancel = () => {
    settled.current = true;
    setEditing(false);
    setError(null);
    setDraft(value);
  };

  if (editing) {
    return (
      <span className="inline-flex flex-col gap-1 min-w-0">
        <Input
          ref={inputRef}
          value={draft}
          aria-label="Design name"
          aria-invalid={error ? true : undefined}
          className="h-8 text-xl font-bold text-slate-800 w-72 max-w-full"
          onChange={e => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onBlur={() => void save()}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              void save();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
        />
        {error && <span className="text-xs font-normal text-destructive">{error}</span>}
      </span>
    );
  }

  if (disabled) {
    return <div className="-my-1 truncate py-1 text-xl leading-7 font-bold text-slate-800">{value}</div>;
  }

  return (
    <div className="min-w-0 leading-7">
      <button
        type="button"
        className={cn(
          "group inline-flex max-w-full items-center gap-1.5 rounded-md text-left text-xl font-bold text-slate-800",
          "cursor-text hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
        aria-label={`Rename ${value}`}
        onClick={() => startEditing(false)}
      >
        {/* Open Sans needs more than this line's height for descenders, so the clip box gets room to spare. */}
        <span className="-my-1 truncate py-1">{value}</span>
        <Pencil className="size-4 shrink-0 text-slate-400 opacity-0 group-hover:opacity-100" aria-hidden="true" />
      </button>
    </div>
  );
};
